// Step 4 of the Fashion route — pick a model template. Replaces the
// Background step from the default flow. Each card shows the model's
// base.png (the photo the user's garment will be composited onto) plus
// a short name + vibe descriptor.
//
// The 3 models live in /Frontend/public/fashion-templates/<folder>/ and
// are enumerated by `fashionModels` in mocks/app-data.ts. base.png is
// 1024×1024; downstream generation uses that as the target image and
// the matching mask_<garmentType>.png as the edit region.
//
// No grouping or pagination needed at v1 — 3 templates fit comfortably
// in a single row.
import { memo, useCallback } from 'react';
import { fashionModels, fashionAssetUrl, type FashionModel } from '@/mocks/app-data';
import StepHeader from './StepHeader';

interface ModelCardProps {
  model: FashionModel;
  isActive: boolean;
  onSelect: (id: string) => void;
}

const ModelCard = memo(function ModelCard({ model, isActive, onSelect }: ModelCardProps) {
  const handleClick = useCallback(() => onSelect(model.id), [model.id, onSelect]);

  return (
    <button
      onClick={handleClick}
      className={`group relative rounded-xl overflow-hidden border-2 text-left cursor-pointer aspect-[3/4] w-full active:scale-[0.98] ${
        isActive
          ? 'border-accent shadow-lg shadow-accent/15'
          : 'border-transparent hover:border-surface-dark/50 bg-surface hover:shadow-sm'
      }`}
    >
      {/* Base photo. cover lets the portrait fill the 3:4 card without
          letterboxing — model templates are 1024×1024 so the top/bottom
          are slightly cropped, which is fine since the subject is
          centered. */}
      <img
        src={fashionAssetUrl(model, model.base)}
        alt={model.name}
        className="absolute inset-0 w-full h-full object-cover"
        loading="eager"
      />
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-dark/80 to-transparent pointer-events-none" />

      <div className="absolute bottom-2 left-2 right-2">
        <p className="text-xs font-bold text-white leading-tight">{model.name}</p>
        <p className="text-[11px] text-white/70 leading-tight">{model.vibe}</p>
      </div>

      {isActive && (
        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-accent flex items-center justify-center">
          <i className="ri-check-line text-white text-xs" />
        </div>
      )}
    </button>
  );
});

interface StepFashionModelProps {
  selected: string | null;
  onSelect: (id: string) => void;
  /** /appv2's Section wrapper supplies its own header and the page
   *  scrolls naturally — drop h-full + the inner scroll wrapper. */
  compact?: boolean;
}

export default function StepFashionModel({ selected, onSelect, compact }: StepFashionModelProps) {
  const selectedModel = fashionModels.find((m) => m.id === selected);

  return (
    <div className={compact ? '' : 'h-full flex flex-col'}>
      {!compact && (
        <StepHeader
          icon="ri-user-line"
          title="Choose a Model"
          subtitle="Pick a model template. We'll detect whether you uploaded a top, bottoms, or shoes and composite the garment onto the matching region."
        />
      )}

      {/* Responsive grid scales with viewport. With 17+ models, we widen
          aggressively so a typical laptop shows ~4 rows without scrolling
          and a wide monitor shows ~3. Cards stay 3:4 portrait so the
          model's body language reads at every size — they just get
          smaller as the column count goes up. */}
      <div className={compact ? '' : 'flex-1 min-h-0 overflow-y-auto'}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {fashionModels.map((model) => (
            <ModelCard
              key={model.id}
              model={model}
              isActive={selected === model.id}
              onSelect={onSelect}
            />
          ))}
        </div>
      </div>

      {selectedModel && (
        <div className="flex-shrink-0 flex items-center justify-center gap-2 pt-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-light border border-primary/20">
            <i className="ri-check-line text-primary text-xs" />
            <span className="text-xs font-semibold text-primary">{selectedModel.name}</span>
            <span className="text-xs text-dark-muted">{selectedModel.vibe}</span>
          </div>
        </div>
      )}
    </div>
  );
}
